import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, CloseThin, Spark } from '../icons';
import { getTimeRemaining, padCountdownUnit } from '../helpers/countdown';
import siteImage from '../img/closing-modal-site.jpg';
import editorLeftImage from '../img/closing-modal-editor-left.jpg';
import editorRightImage from '../img/closing-modal-editor-right.jpg';

// Announces the shutdown of this old order app and counts down to it. Sibling of
// NewUiBanner: the banner nudges, this modal interrupts. Once the deadline has
// passed the modal becomes permanent and undismissable, so the dead site cannot
// be used without seeing where it moved to.
const NEW_SITE_URL = 'https://www.sweetpixtiles.com/';
const DISMISSED_KEY = 'sweetpix.siteClosingModalDismissed';

// Decorative sparkles. Positions live in the stylesheet, keyed by these letters.
const SPARKS = ['a', 'b', 'c', 'd', 'e', 'f'];

// sessionStorage access throws when cookies are blocked (e.g. Safari private
// mode), where a modal that fails open is better than one that crashes the app.
function readDismissed() {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

function storeDismissed() {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, 'true');
  } catch (error) {
    // Dismissal simply does not persist past this render.
  }
}

export default function SiteClosingModal() {
  const [remaining, setRemaining] = useState(getTimeRemaining);
  const [dismissed, setDismissed] = useState(readDismissed);

  const isClosed = remaining.expired;
  const canDismiss = !isClosed;

  useEffect(() => {
    if (isClosed) {
      return undefined;
    }

    const timer = setInterval(() => setRemaining(getTimeRemaining()), 1000);

    return () => clearInterval(timer);
  }, [isClosed]);

  const dismiss = useCallback(() => {
    storeDismissed();
    setDismissed(true);
  }, []);

  useEffect(() => {
    if (!canDismiss) {
      return undefined;
    }

    const onKeyDown = event => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [canDismiss, dismiss]);

  if (dismissed && canDismiss) {
    return null;
  }

  const units = [
    { label: 'Days', value: remaining.days },
    { label: 'Hours', value: remaining.hours },
    { label: 'Mins', value: remaining.minutes },
    { label: 'Secs', value: remaining.seconds }
  ];

  return (
    <div className="modal-overlay">
      <div
        className="closing-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="closing-modal-headline"
      >
        <div className="closing-modal__inner">
          <div className="closing-modal__art" aria-hidden="true">
            <img className="closing-modal__art-tablet closing-modal__art-tablet--left" src={editorLeftImage} alt="" />
            <img className="closing-modal__art-tablet closing-modal__art-tablet--right" src={editorRightImage} alt="" />
            <div className="closing-modal__art-site">
              <img src={siteImage} alt="" />
            </div>
            {SPARKS.map(spark => (
              <span key={spark} className={`closing-modal__spark closing-modal__spark--${spark}`}>
                <Spark />
              </span>
            ))}
          </div>

          <div className="closing-modal__copy">
            <h2 className="closing-modal__headline" id="closing-modal-headline">
              We’re moving to our<br />new website
            </h2>
            <p className="closing-modal__sub">
              {isClosed
                ? 'Our old website is now closed.'
                : 'Our old website is closing soon.'}
              {' '}
              Please continue your SweetPixTiles experience on our new website.
            </p>
            {!isClosed && (
              <p className="closing-modal__promo">
                <span className="closing-modal__promo-spark" aria-hidden="true">
                  <Spark />
                </span>
                Get 20% off when you switch to the new site.
              </p>
            )}
          </div>

          {!isClosed && (
            <div className="closing-modal__countdown">
              {units.map((unit, index) => (
                <React.Fragment key={unit.label}>
                  {index > 0 && <span className="closing-modal__countdown-separator" aria-hidden="true">:</span>}
                  <div className="closing-modal__countdown-unit">
                    <span className="closing-modal__countdown-value">{padCountdownUnit(unit.value)}</span>
                    <span className="closing-modal__countdown-label">{unit.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

          <a className="closing-modal__cta" href={NEW_SITE_URL}>
            Go to new website
            <ArrowRight />
          </a>
        </div>

        {canDismiss && (
          <button className="closing-modal__close" type="button" onClick={dismiss} aria-label="Dismiss">
            <CloseThin />
          </button>
        )}
      </div>
    </div>
  );
}
